import debounce from 'lodash/debounce';
import upperFirst from 'lodash/upperFirst';
import { track } from '../facade';
import product from 'src/common/config/product';

// default is set for pages without slugs, that are
// only LMN
let _sku = product.skus.LMN;
let _context = 'Misc';

const dispatch = (action, label = null, value = null) => track(action, {
  category: `Product - ${_sku} - ${_context}`,
  label,
  value
});

export const init = (sku, context) => {
  _sku = sku || _sku;
  _context = context || _context;
};

export const trackSaveForLaterClickCta = () => dispatch('Save For Later Form - Clicked CTA');
export const trackSaveForLaterOpen = () => dispatch('Save For Later Form - Open Modal');
export const trackSaveForLaterClose = () => dispatch('Save For Later Form - Close Modal');
export const trackSaveForLaterEmailInput = debounce(() => dispatch('Save For Later Form - Typed email'), 500);
export const trackSaveForLaterSubmitted = () => dispatch('Save For Later Form - Submitted');

export const trackAdventurerChange = phototype => dispatch('Adventurer Drawer - Change Adventurer', phototype);
export const trackAdventurerDrawerSaveTab = () => dispatch('Adventurer Drawer', 'Save Tab');
export const trackBookLanguageChange = () => dispatch('Adventurer Drawer - Click into Book Language', 'Click Drop Down');
export const trackNameChange = debounce(name => dispatch('Adventurer Drawer - Enter Child Name', name), 500);

export const trackSecondNameChange = debounce(() => dispatch('Adventurer Drawer - Enter Child Name Second Partial'), 500);
export const trackGenderChange = gender => dispatch('Adventurer Drawer - Select Gender', gender);
export const trackAgeChange = age => dispatch('Adventurer Drawer - Select Age', age);
export const trackSecondGenderChange = gender => dispatch('Adventurer Drawer - Select Gender Second Partial', gender);
export const trackHeroImageCarousel = activeIndex => dispatch('Hero Image Carousel', `Clicked Photo ${activeIndex}`);

export const trackSelectionFormChange = selectionState => dispatch('Selection Form - Change State', selectionState);
export const trackSelectionFormInputChange = selectionValue => dispatch('Selection Form - Change Of Custom Input', selectionValue);

/**
 * trackDrawerAction dispatch a tracking event to do with a drawers actions
 * @param {string} drawerType the type of the drawer
 * @param {string} action the type of trigger that happened
 * @returns {Promise<[string | Object]>}
 * @example
 * trackDrawerAction('adventurer', 'Open');
 * => ['track', 'Adventurer Drawer', { category: 'Product - lmn - Canvas Page', label: 'Open Tab', value: null }]
 */
const trackDrawerAction = (drawerType, action) => {
  const drawerName = upperFirst(drawerType);
  return dispatch(`${drawerName} Drawer`, `${action} Tab`);
};
export const trackDrawerOpenTab = drawerType => trackDrawerAction(drawerType, 'Open');
export const trackDrawerCloseTab = drawerType => trackDrawerAction(drawerType, 'Close');
export const trackDrawerSaveTab = drawerType => trackDrawerAction(drawerType, 'Save');

export const trackQualitiesDrawerOpenTab = () => dispatch('Qualities Drawer', 'Open Tab');
export const trackQualitiesDrawerCloseTab = () => dispatch('Qualities Drawer', 'Close Tab');
export const trackQualitiesDrawerSelected = (quality, removed) => dispatch('Qualities Drawer', `Select ${quality}`, removed ? 'Removed' : 'Added');
export const trackQualitiesDrawerErrored = numberOfQualities => dispatch('Qualities Drawer', 'Qualities Errored', `${numberOfQualities} selected`);

export const trackFamilyDrawerOpenTab = () => dispatch('Family Drawer', 'Open Tab');
export const trackFamilyDrawerCloseTab = () => dispatch('Family Drawer', 'Close Tab');
export const trackFamilyDrawerSaveTab = () => dispatch('Family Drawer', 'Save Tab');
export const trackFamilyMembersAdd = () => dispatch('Family member', 'Add');
export const trackFamilyMembersChanged = debounce(() => dispatch('Family member', 'Changed'), 500);
export const trackFamilyMembersNameTooShort = debounce(() => dispatch('Family member', 'Name Too Short'), 500);
export const trackFamilyMembersNameTooLong = debounce(() => dispatch('Family member', 'Name Too Long'), 500);
export const trackFamilyMembersNameWrongCharacters = debounce(() => dispatch('Family member', 'Name Too Long'), 500);
export const trackFamilyMembersRemove = () => dispatch('Family member', 'Remove');
export const trackFamilyMembersDeleteAll = debounce(() => dispatch('Family member', 'Delete All'), 500);

export const trackDedicationDrawerOpenTab = () => dispatch('Dedication Drawer', 'Open Tab');
export const trackDedicationDrawerCloseTab = () => dispatch('Dedication Drawer', 'Close Tab');
export const trackDedicationDrawerSaveTab = () => dispatch('Dedication Drawer', 'Save Tab');
export const trackDedicationDrawerDeleteAll = debounce(() => dispatch('Dedication Drawer', 'Delete All'), 500);
export const trackDedicationDrawerChanged = debounce(() => dispatch('Dedication Drawer', 'Type'), 500);

export const trackFormatDrawerOpenTab = () => dispatch('Format Drawer', 'Open Tab');
export const trackFormatDrawerCloseTab = () => dispatch('Format Drawer', 'Close Tab');
export const trackFormatDrawerSaveTab = () => dispatch('Format Drawer', 'Save Tab');
export const trackFormatDrawerChanged = () => dispatch('Format Drawer', 'Changed');

export const trackMultiAdventurerDrawerOpenTab = () => dispatch('Multi Adventurer Drawer', 'Open Tab');
export const trackMultiAdventurerDrawerCloseTab = () => dispatch('Multi Adventurer Drawer', 'Close Tab');
export const trackMultiAdventurerDrawerSaveTab = () => dispatch('Multi Adventurer Drawer', 'Save Tab');
export const trackMultiAdventurerDrawerCutomize = () => dispatch('Multi Adventurer Drawer', 'Customize Tab');
export const trackMultiAdventurerAddNew = id => dispatch('Multi Adventurer Drawer', 'Add New Adventurer Tab', id);
export const trackMultiAdventurerDrawerSaveAdventurer = id => dispatch('Multi Adventurer Drawer', 'Save Adventurer Tab', id);
export const trackMultiAdventurerNameChange = (id) => debounce(name => {
  dispatch(`Multi Adventurer - Enter Child Name - ${id}`, name);
}, 500);
export const trackMiultiAdventurerGenderChange = (id) => gender => {
  dispatch(`Multi Adventurer Drawer - Select Gender for ${id} adventurer`, gender);
};
export const trackMultiAdventurerPhototypeChange = (id) => phototype => {
  dispatch(`Multi Adventurer Drawer - Select Phototype for ${id} adventurer`, phototype);
};

export const trackCreateBookCTA = () => dispatch('Create Book CTA');
export const trackBreadcrumbClick = breadcrumbLabel => dispatch('Breadcrumb', breadcrumbLabel);

export const trackAddOnsPageVisited = pageNum => dispatch('AddOns - Visited Page', `Page ${pageNum}`);
export const trackAddOnsSelected = addOnId => dispatch('AddOns - Selected', addOnId);
export const trackAddOnsDeselected = addOnId => dispatch('AddOns - Deselected', addOnId);

const trackMultiBirthdaySelectedValue = (childId, fieldName, fieldValue) => dispatch('Multi-birthday Drawer', `selected ${fieldName} for child ${childId}`, fieldValue);
export const trackMultiBirthdaySelectedDay = childId => value => trackMultiBirthdaySelectedValue(childId, 'day', value);
export const trackMultiBirthdaySelectedMonth = childId => value => trackMultiBirthdaySelectedValue(childId, 'month', value);
export const trackMultiBirthdaySelectedYear = childId => value => trackMultiBirthdaySelectedValue(childId, 'year', value);

export const trackMultiInputAdd = () => dispatch('Multi Input', 'Add');
export const trackMultiInputRemove = () => dispatch('Multi Input', 'Remove');
export const trackMultiInputChanged = debounce(() => dispatch('Multi Input', 'Changed'), 500);
export const trackMultiInputDrawerSaveTab = () => dispatch('Multi Input', 'Save Tab');

export const trackCharactersAdd = () => dispatch('Characters Drawer', 'Add another characters');
export const trackCharactersModalShown = () => dispatch('Characters Drawer', 'Unable to delete character modal shown');
export const trackCharactersDeleteLast = () => dispatch('Characters Drawer', 'Delete last character');
export const trackCharactersKeepAll = () => dispatch('Characters Drawer', 'Keep all characters');

export const trackCreationFlowNextStep = (currentStep) => {
  const previousStep = window.creationFlowPreviousStep || currentStep - 1;
  if (previousStep === currentStep) {
    return;
  }
  const isMovingBack = Math.sign(currentStep - previousStep) === -1;
  const directionString = isMovingBack ? 'Moving back to step' : 'Moving to step';
  dispatch('Creation Flow', `${directionString} ${currentStep + 1}`);
};
export const tractCreationFlowBackToStepGenerator = () => {
  dispatch('Creation Flow', 'Moving back to create steps');
};
export const trackCreationFlowRedirectTo = (page) => dispatch('Creation Flow', `Redirecting to ${page}`);
